import { createElement, Fragment, type ReactNode } from 'react';

/* Часть именованных сущностей (полный список не нужен — контент админский) */
const NAMED_ENTITIES: Record<string, string> = {
  nbsp: '\u00A0', // неразрывный пробел, как в HTML
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  copy: '©',
  reg: '®',
  trade: '™',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  laquo: '«',
  raquo: '»',
  deg: '°',
};

/**
 * Декодирование HTML-сущностей в тексте: сериализатор MDXEditor экранирует
 * пробел в начале/конце абзаца как `&#x20;` (валидный CommonMark), и мобильное
 * приложение (react-native-markdown-display) декодирует его в пробел — превью
 * должно показывать то же самое. Код-спаны и блоки кода не декодируются
 * (по спецификации CommonMark сущности внутри кода — литералы).
 */
function decodeEntities(text: string): string {
  return text.replace(/&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (match, body: string) => {
    let code: number;
    if (body.startsWith('#x') || body.startsWith('#X')) code = parseInt(body.slice(2), 16);
    else if (body.startsWith('#')) code = parseInt(body.slice(1), 10);
    else return NAMED_ENTITIES[body] ?? match;
    if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return match;
    return String.fromCodePoint(code);
  });
}

function inlineNodes(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re =
    /(!\[[^\]]*\]\([^)\s]+(?:\s+"[^"]*")?\))|(\[[^\]]+\]\([^)\s]+(?:\s+"[^"]*")?\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(__[^_]+__)|(_[^_]+_)|(`[^`]+`)/g;
  let last = 0;
  let key = 0;

  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) nodes.push(decodeEntities(text.slice(last, m.index)));

    if (m[1]) {
      const im = m[1].match(/!\[([^\]]*)\]\(([^)\s]+)/);
      if (im) {
        nodes.push(
          <img key={key++} src={im[2]} alt={decodeEntities(im[1])} className="markdown-image" />,
        );
      }
    } else if (m[2]) {
      const lm = m[2].match(/\[([^\]]+)\]\(([^)\s]+)/);
      if (lm) {
        nodes.push(
          <a key={key++} href={lm[2]} target="_blank" rel="noreferrer" className="markdown-link">
            {decodeEntities(lm[1])}
          </a>,
        );
      }
    } else if (m[3] || m[5]) {
      const inner = (m[3] ?? m[5])!;
      nodes.push(<strong key={key++}>{inlineNodes(inner.slice(2, -2))}</strong>);
    } else if (m[4] || m[6]) {
      const inner = (m[4] ?? m[6])!;
      nodes.push(<em key={key++}>{inlineNodes(inner.slice(1, -1))}</em>);
    } else if (m[7]) {
      nodes.push(
        <code key={key++} className="markdown-code-inline">
          {m[7].slice(1, -1)}
        </code>,
      );
    }

    last = re.lastIndex;
  }

  if (last < text.length) nodes.push(decodeEntities(text.slice(last)));
  return nodes;
}

function isBlockStart(line: string): boolean {
  return (
    /^#{1,6}\s/.test(line) ||
    /^\s*[-*+]\s+/.test(line) ||
    /^\s*\d+[.)]\s+/.test(line) ||
    /^\s*>\s/.test(line) ||
    line.trimStart().startsWith('```') ||
    /^\s*([-*_])\s*(\1\s*){2,}$/.test(line)
  );
}

export function MarkdownPreview({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    if (line.trimStart().startsWith('```')) {
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1;
      blocks.push(
        <pre key={key++} className="markdown-code-block">
          <code>{buf.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push(
        createElement(
          `h${level}`,
          { key: key++, className: `markdown-h markdown-h${level}` },
          inlineNodes(heading[2]),
        ),
      );
      i += 1;
      continue;
    }

    if (/^\s*([-*_])\s*(\1\s*){2,}$/.test(line)) {
      blocks.push(<hr key={key++} className="markdown-hr" />);
      i += 1;
      continue;
    }

    if (/^\s*>\s/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i]) && lines[i].trim() !== '') {
        buf.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push(
        <blockquote key={key++} className="markdown-quote">
          {buf.map((l, idx) => (
            <p key={idx}>{inlineNodes(l)}</p>
          ))}
        </blockquote>,
      );
      continue;
    }

    const ordered = /^\s*\d+[.)]\s+/.test(line);
    if (ordered || /^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (ordered) {
          const m = cur.match(/^\s*\d+[.)]\s+(.*)$/);
          if (!m) break;
          items.push(m[1]);
        } else {
          const m = cur.match(/^\s*[-*+]\s+(.*)$/);
          if (!m) break;
          items.push(m[1]);
        }
        i += 1;
      }
      blocks.push(
        createElement(
          ordered ? 'ol' : 'ul',
          { key: key++, className: ordered ? 'markdown-ol' : 'markdown-ul' },
          items.map((it, idx) => <li key={idx}>{inlineNodes(it)}</li>),
        ),
      );
      continue;
    }

    const buf: string[] = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines[i])) {
      buf.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++} className="markdown-p">
        {buf.map((l, idx) => (
          <Fragment key={idx}>
            {idx > 0 ? <br /> : null}
            {inlineNodes(l)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <div className="markdown-preview">{blocks}</div>;
}
